import {
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, {
  useState,
  useMemo,
  useEffect,
  useContext,
  forwardRef,
  useImperativeHandle
} from 'react';
import axios from 'src/axios';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import { SnackContext, AppContext } from 'src/store/ContextStore';
import { calculateAgeOnDate } from 'src/util/util';
import TextInput from 'src/components/UI/input/TextInput';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const ButtonGroup = styled(Grid)({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 15
});

export const StyledLabel = styled(FormLabel)({
  lineHeight: 1.3
});

const InfoAgeLabel = styled('span')({
  fontSize: 10,
  opacity: 0.8
});

/**
 * Gail (BCRAT) risk-factor INPUT form. Self-contained: manages its own field
 * state, loads any existing gail record, and saves the inputs (no score) via
 * `gail-inputs`. Used by the Clinical Records "Risk Factor" modal. The score is
 * calculated separately from the Risk Scores sidebar (GailScoreModal).
 *
 * Props:
 * - data        — selected pedigree node (gender, dob, pid, user_id).
 * - onSaved     — called after inputs are saved.
 * - disabled    — read-only mode.
 * - hideActionButton — hide the in-form Save button; the parent triggers the
 *   save via the imperative `submit()` handle (used by the modal footer).
 */
const GailForm = forwardRef(function GailForm(
  { data, model, onSaved, disabled = false, hideActionButton = false },
  ref
) {
  const t = useAutoTranslation();

  const { setSnack } = useContext(SnackContext);
  const { content } = useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;

  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState({
    isBreastCancer: false,
    isMutation: false
  });

  const user_id = useMemo(() => user?.user_id || user?._id, [user]);
  const gender = useMemo(() => data?.gender, [data]);
  const familyMemberId = useMemo(() => data?.familyMemberId, [data]);
  const proBandId = useMemo(() => data?.proBandId, [data]);
  // Gail records are stored against this id; prefill must read by the same id.
  const memberId = useMemo(
    () => proBandId ?? familyMemberId,
    [proBandId, familyMemberId]
  );

  const init = () => {
    let dob = data?.dob;
    if (dob) {
      // Today's calendar age (calculateAgeOnDate), the Gail model's "current
      // age". calculateAge counted elapsed time from a UTC-midnight birth date,
      // so on the birthday before the offset hour it was a year short in
      // Sydney, and it could be a year out near a birthday elsewhere.
      let age = calculateAgeOnDate(dob, new Date());
      setFormValues((pre) => ({ ...pre, age: String(age) }));
    }
  };

  const applyRecord = (rec) => {
    if (!rec) return;
    const {
      isBreastCancer,
      isMutation,
      age,
      ageMen,
      nBiops,
      ageFLB,
      numRel,
      race
    } = rec;

    setFormValues({
      isBreastCancer: isBreastCancer,
      isMutation: isMutation,
      age: String(age),
      ageMen: String(ageMen),
      nBiops: String(nBiops),
      ageFLB: String(ageFLB),
      numRel: String(numRel),
      race: race
    });
  };

  const getRiskFactor = () => {
    // user_id is required by the endpoint: it is what the ownership check
    // authorises against (a bare familyMemberId is a guessable id shape).
    if (!memberId || !user_id) return;
    axios
      .get(
        `${APICONSTANTS.getRiskFactor.replace(
          ':pid',
          memberId
        )}?recordType=gail&user_id=${user_id}`
      )
      .then((res) => applyRecord(res.data))
      .catch(() => {});
  };

  useEffect(() => {
    init();
    // Editing a specific record prefills from it; a new record starts from the
    // member's latest saved inputs as a convenience.
    if (model?._id) {
      applyRecord(model);
    } else {
      getRiskFactor();
    }
    // user_id is included because the prefill fetch now requires it: it can
    // resolve from context after `data`, and without it the fetch would be
    // skipped for good.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, model?._id, user_id]);

  const handleChange = (e) => {
    const value =
      typeof e.target.value === 'string'
        ? e.target.value.trim()
        : e.target.value;

    if (e.target.type === 'radio') {
      const radioValue = e.target.value === 'true';
      setFormValues((prevState) => ({
        ...prevState,
        [e.target.name]: radioValue
      }));
    } else {
      setFormValues((prevState) => ({
        ...prevState,
        [e.target.name]: value
      }));
    }
  };

  // Persist the Gail inputs (no score). The score is calculated later from the
  // Risk Scores sidebar (GailScoreModal).
  const saveGailInputsHandle = () => {
    if (!memberId || !user_id)
      return setSnack({
        status: true,
        msg: t('Please save the pedigree first'),
        severity: 'error'
      });

    setLoading(true);
    axios
      .post(APICONSTANTS.saveGailInputs, {
        // Editing an existing record updates it; otherwise create a new one.
        recordId: model?._id,
        isBreastCancer: formValues.isBreastCancer,
        isMutation: formValues.isMutation,
        age: formValues.age ? Number(formValues.age) : undefined,
        ageMen: formValues.ageMen ? Number(formValues.ageMen) : undefined,
        nBiops: formValues.nBiops ? Number(formValues.nBiops) : undefined,
        ageFLB: formValues.ageFLB ? Number(formValues.ageFLB) : undefined,
        numRel: formValues.numRel ? Number(formValues.numRel) : undefined,
        race: formValues.race,
        user_id,
        familyId: user.familyId,
        familyMemberId: memberId
      })
      .then((resp) => {
        setSnack({
          status: true,
          msg: t('Risk factor inputs saved'),
          severity: 'success'
        });
        onSaved?.(resp.data);
      })
      .catch(() => {
        setSnack({
          status: true,
          msg: t('An error has occurred!'),
          severity: 'error'
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Let a parent (e.g. the Risk Factor modal footer) trigger the save.
  useImperativeHandle(ref, () => ({
    submit: () => saveGailInputsHandle()
  }));

  const isSaveDisabled = disabled || loading;

  return (
    <Grid container spacing={2}>
      {/* isBreastCancer */}
      {gender === 'female' && (
        <Grid size={12}>
          <FormControl component="fieldset">
            <StyledLabel component="label">
              {t(
                'Does the woman have a medical history of any breast cancer or of ductal carcinoma in situ (DCIS) or lobular carcinoma in situ (LCIS) or has she received previous radiation therapy to the chest for treatment of Hodgkin lymphoma?'
              )}
            </StyledLabel>
            <RadioGroup
              aria-label="isBreastCancer"
              name="isBreastCancer"
              value={String(formValues.isBreastCancer)}
              onChange={handleChange}
              row
            >
              <FormControlLabel
                value="true"
                control={<Radio size="small" disabled={disabled} />}
                label={t('Yes')}
              />
              <FormControlLabel
                value="false"
                control={<Radio size="small" disabled={disabled} />}
                label={t('No')}
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      )}
      {/* isMutation */}
      {gender === 'female' && (
        <Grid size={12}>
          <FormControl component="fieldset">
            <StyledLabel component="label">
              {t(
                'Does the woman have a mutation in either the BRCA1 or BRCA2 gene, or a diagnosis of a genetic syndrome that may be associated with elevated risk of breast cancer?'
              )}
            </StyledLabel>
            <RadioGroup
              aria-label="isMutation"
              name="isMutation"
              value={String(formValues.isMutation)}
              onChange={handleChange}
              row
            >
              <FormControlLabel
                value="true"
                control={<Radio size="small" disabled={disabled} />}
                label={t('Yes')}
              />
              <FormControlLabel
                value="false"
                control={<Radio size="small" disabled={disabled} />}
                label={t('No')}
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      )}
      {/* age */}
      {gender === 'female' && (
        <Grid size={12}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Grid size={12}>
                <FormLabel component="label" className="form-label">
                  Current Age
                </FormLabel>
              </Grid>

              <TextField
                size="small"
                name="age"
                value={formValues.age}
                onChange={handleChange}
                type="number"
                disabled={disabled}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
      {/* race */}
      {gender === 'female' && (
        <Grid size={12}>
          <div className="margin-top-4"></div>
          <FormControl component="fieldset" style={{ width: '100%' }}>
            <TextInput
              label="Race/Ethnicity?"
              type="select"
              name="race"
              value={formValues.race}
              onChange={handleChange}
              options={CONSTANTS.raceOptions}
              disabled={disabled}
            />
          </FormControl>
        </Grid>
      )}
      {/* biopsies */}
      {gender === 'female' && (
        <Grid size={12}>
          <div className="margin-top-4"></div>
          <FormControl component="fieldset" style={{ width: '100%' }}>
            <TextInput
              label="Number of breast biopsies?"
              type="select"
              name="nBiops"
              value={formValues.nBiops}
              onChange={handleChange}
              options={CONSTANTS.biopsiesOptions}
              disabled={disabled}
            />
          </FormControl>
        </Grid>
      )}
      {/* menstruation */}
      {gender === 'female' && (
        <Grid size={12}>
          <div className="margin-top-4"></div>
          <FormControl component="fieldset" style={{ width: '100%' }}>
            <TextInput
              label="Age of first menstruation?"
              type="select"
              name="ageMen"
              value={formValues.ageMen}
              onChange={handleChange}
              options={CONSTANTS.menstruationOptions}
              disabled={disabled}
            />
          </FormControl>
        </Grid>
      )}
      {/* first birth */}
      {gender === 'female' && (
        <Grid size={12}>
          <div className="margin-top-4"></div>
          <FormControl component="fieldset" style={{ width: '100%' }}>
            <TextInput
              label="Age at first birth?"
              type="select"
              name="ageFLB"
              value={formValues.ageFLB}
              onChange={handleChange}
              options={CONSTANTS.flbOptions}
              disabled={disabled}
            />
          </FormControl>
        </Grid>
      )}
      {/* numRel */}
      {gender === 'female' && (
        <Grid size={12}>
          <div className="margin-top-4"></div>
          <FormControl component="fieldset" style={{ width: '100%' }}>
            <TextInput
              label="Number of 1st degree relatives that have had breast cancer?"
              type="select"
              name="numRel"
              value={formValues.numRel}
              onChange={handleChange}
              options={CONSTANTS.relativeOptions}
              disabled={disabled}
            />
          </FormControl>
        </Grid>
      )}
      {!hideActionButton && gender === 'female' && (
        <ButtonGroup size={12}>
          <Button
            variant="contained"
            color="secondary"
            onClick={saveGailInputsHandle}
            disabled={isSaveDisabled}
            loading={loading}
            sx={{ width: '100%' }}
          >
            {t('Save')}
          </Button>
        </ButtonGroup>
      )}
    </Grid>
  );
});

export default GailForm;
